import React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";

interface AppButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  variant?: "primary" | "secondary" | "outline" | "ghost" | "link";
  size?: "sm" | "md" | "lg";
  to?: string;
  external?: boolean;
  withArrow?: boolean;
  children: React.ReactNode;
}

// 辅助函数：处理页内锚点平滑滚动
const scrollToElement = (id: string, event: React.MouseEvent) => {
  event.preventDefault();
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      to,
      external = false,
      withArrow = false,
      children,
      ...props
    },
    ref
  ) => {
    const baseClasses = cn(
      "inline-flex items-center justify-center rounded-md font-medium transition-all duration-300 focus-ring",
      {
        // Variants
        "bg-anzir text-anzir-foreground hover:bg-anzir/90": variant === "primary",
        "bg-secondary text-secondary-foreground hover:bg-secondary/80": variant === "secondary",
        "border border-border bg-transparent hover:bg-secondary": variant === "outline",
        "hover:bg-secondary": variant === "ghost",
        "text-foreground underline-offset-4 hover:underline": variant === "link",
        
        // Sizes
        "text-sm px-3 py-1.5": size === "sm",
        "text-base px-5 py-2.5": size === "md",
        "text-lg px-6 py-3": size === "lg",
      },
      className
    );

    const content = (
      <>
        {children}
        {withArrow && <ArrowRight className="ml-2 h-4 w-4" />}
      </>
    );

    if (to) {
      // 处理页内锚点链接
      if (to.startsWith('#')) {
        const elementId = to.substring(1); // 移除 # 符号
        return (
          <a
            href={to}
            className={baseClasses}
            onClick={(e) => scrollToElement(elementId, e)}
            {...(props as any)}
          >
            {content}
          </a>
        );
      }
      
      // 处理外部链接
      if (external) {
        return (
          <a
            href={to}
            className={baseClasses}
            target="_blank"
            rel="noopener noreferrer"
            {...(props as any)}
          >
            {content}
          </a>
        );
      }
      
      // 处理内部路由链接
      return (
        <Link to={to} className={baseClasses} {...(props as any)}>
          {content}
        </Link>
      );
    }

    return (
      <button className={baseClasses} ref={ref} {...props}>
        {content}
      </button>
    );
  }
);

AppButton.displayName = "AppButton";

export default AppButton;
